import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from '../../store/hooks';
import {logoutSuccess} from '../../store/slice/authSlice';
import {
  getUserInfo,
  updateAccountSettings,
  updatePassword,
  deleteMyAccount,
  requestEmailChange,
  getSocialLinkUrl,
  unlinkSocialAccount,
  type SocialProvider,
  type UserProfileDto,
} from '../../api/userApi';
import {
  disableWebPush,
  disableWebPushLocalOnly,
} from '../../components/live/webPushClient';

type AccountSettingsForm = {
  phone: string;
  marketingEmailAgree: boolean;
  marketingSmsAgree: boolean;
};

export function useAccountSettings() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfileDto | null>(null);

  const [accountOpen, setAccountOpen] = useState(false);
  const [passwordEditOpen, setPasswordEditOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [socialLoadingProvider, setSocialLoadingProvider] = useState<SocialProvider | null>(null);

  const clearMessages = useCallback(() => {
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const data = await getUserInfo();
      setProfile(data ?? null);
      return data;
    } catch {
      setProfile(null);
      return null;
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  const openAccountSettings = useCallback(async () => {
    clearMessages();
    await loadProfile();
    setAccountOpen(true);
  }, [clearMessages, loadProfile]);

  const closeAccountSettings = useCallback(() => {
    setAccountOpen(false);
  }, []);

  const openPasswordEdit = useCallback(() => {
    clearMessages();
    setAccountOpen(false);
    setPasswordEditOpen(true);
  }, [clearMessages]);

  const closePasswordEdit = useCallback(() => {
    setPasswordEditOpen(false);
  }, []);

  const openWithdrawConfirm = useCallback(() => {
    clearMessages();
    setAccountOpen(false);
    setWithdrawOpen(true);
  }, [clearMessages]);

  const closeWithdrawConfirm = useCallback(() => {
    if (withdrawing) return;
    setWithdrawOpen(false);
  }, [withdrawing]);

  const openEmailChange = useCallback(() => {
    clearMessages();
    setAccountOpen(false);
    setEmailChangeOpen(true);
  }, [clearMessages]);

  const closeEmailChange = useCallback(() => {
    if (changingEmail) return;
    setEmailChangeOpen(false);
  }, [changingEmail]);

  const saveAccountSettings = useCallback(
      async (form: AccountSettingsForm) => {
        try {
          setSavingAccount(true);
          clearMessages();

          const updated = await updateAccountSettings({
            phone: form.phone,
            marketingEmailAgree: form.marketingEmailAgree,
            marketingSmsAgree: form.marketingSmsAgree,
          });

          setProfile(updated ?? null);
          setAccountOpen(false);
          setSuccessMessage('계정 설정이 저장되었습니다.');
        } catch {
          setErrorMessage('계정 설정 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
          setSavingAccount(false);
        }
      },
      [clearMessages],
  );

  const changePassword = useCallback(
      async (form: { oldPassword: string; newPassword: string }) => {
        try {
          setChangingPassword(true);
          clearMessages();

          await updatePassword(form.oldPassword, form.newPassword);

          setPasswordEditOpen(false);
          setSuccessMessage('비밀번호가 성공적으로 변경되었습니다.');
        } catch {
          setErrorMessage('비밀번호 변경에 실패했습니다. 현재 비밀번호를 다시 확인해 주세요.');
        } finally {
          setChangingPassword(false);
        }
      },
      [clearMessages],
  );

  const changeEmail = useCallback(
      async (newEmail: string) => {
        try {
          setChangingEmail(true);
          clearMessages();

          const result = await requestEmailChange({newEmail});

          setEmailChangeOpen(false);
          setSuccessMessage(
              result?.message || '이메일 변경 요청이 접수되었습니다. 새 이메일에서 인증을 완료해 주세요.',
          );

          await loadProfile();
        } catch {
          setErrorMessage('이메일 변경 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
          setChangingEmail(false);
        }
      },
      [clearMessages, loadProfile],
  );

  const getConnectedLoginMethodCount = useCallback(() => {
    return [
      Boolean(profile?.googleConnected),
      Boolean(profile?.kakaoConnected),
      Boolean(profile?.naverConnected),
    ].filter(Boolean).length;
  }, [profile]);

  const connectSocialAccount = useCallback(
      async (provider: SocialProvider) => {
        try {
          setSocialLoadingProvider(provider);
          clearMessages();

          const result = await getSocialLinkUrl(provider);

          if (!result?.url) {
            throw new Error('연동 URL이 없습니다.');
          }

          window.location.assign(result.url);
        } catch {
          setErrorMessage('소셜 계정 연동 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
          setSocialLoadingProvider(null);
        }
      },
      [clearMessages],
  );

  const disconnectSocialAccount = useCallback(
      async (provider: SocialProvider) => {
        try {
          clearMessages();

          const connectedCount = getConnectedLoginMethodCount();

          if (connectedCount <= 1) {
            setErrorMessage('마지막 로그인 수단은 해제할 수 없습니다. 다른 로그인 수단을 먼저 연결하세요.');
            return;
          }

          setSocialLoadingProvider(provider);

          const result = await unlinkSocialAccount(provider);

          setSuccessMessage(result?.message || '소셜 계정 연결이 해제되었습니다.');
          await loadProfile();
        } catch {
          setErrorMessage('소셜 계정 연결 해제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
          setSocialLoadingProvider(null);
        }
      },
      [clearMessages, getConnectedLoginMethodCount, loadProfile],
  );

  const confirmWithdraw = useCallback(async () => {
    try {
      setWithdrawing(true);
      clearMessages();

      /*
       * 회원탈퇴 전에 현재 로그인 자격으로 서버 Web Push subscription을 먼저
       * 비활성화하고 브라우저 PushSubscription도 해제한다.
       *
       * Web Push 정리 실패가 계정 삭제 자체를 막아서는 안 된다.
       * disableWebPush()는 서버 unsubscribe 실패 시에도 로컬 unsubscribe를
       * 시도하지만, 예외 경로에서도 local-only 해제를 한 번 더 보장한다.
       */
      try {
        await disableWebPush();
      } catch (pushError) {
        console.warn(
            '[PolicyIntel] Web Push cleanup before account withdrawal failed',
            pushError,
        );

        try {
          await disableWebPushLocalOnly();
        } catch (localPushError) {
          console.warn(
              '[PolicyIntel] Local Web Push cleanup before account withdrawal failed',
              localPushError,
          );
        }
      }

      /*
       * deleteMyAccount()가 성공하면 client auth가 정리된다.
       * Web Push unsubscribe를 이 호출 뒤에 두면 인증이 사라져
       * /web-push/unsubscribe가 401이 될 수 있으므로 반드시 위에서 수행한다.
       */
      await deleteMyAccount();

      dispatch(logoutSuccess());

      setProfile(null);
      setWithdrawOpen(false);
      setAccountOpen(false);
      setPasswordEditOpen(false);
      setEmailChangeOpen(false);

      /*
       * 계정 삭제가 완료된 뒤에만 다른 탭/팝업에 logout을 전파한다.
       * App.tsx의 auth-event listener가 다른 탭의 Redux auth state도 정리한다.
       */
      try {
        localStorage.setItem(
            'policy-intel:auth-event',
            JSON.stringify({
              type: 'logout',
              reason: 'account-withdrawal',
              at: Date.now(),
            }),
        );
      } catch {
        // 저장소가 제한된 환경에서도 현재 창의 탈퇴 완료 처리는 유지한다.
      }

      navigate('/', {replace: true});
    } catch {
      setErrorMessage('회원탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setWithdrawing(false);
    }
  }, [clearMessages, dispatch, navigate]);

  return {
    profile,

    accountOpen,
    passwordEditOpen,
    withdrawOpen,
    emailChangeOpen,

    loadingProfile,
    savingAccount,
    changingPassword,
    withdrawing,
    changingEmail,

    successMessage,
    errorMessage,

    socialLoadingProvider,

    clearMessages,
    loadProfile,

    openAccountSettings,
    closeAccountSettings,

    openPasswordEdit,
    closePasswordEdit,

    openWithdrawConfirm,
    closeWithdrawConfirm,

    openEmailChange,
    closeEmailChange,

    saveAccountSettings,
    changePassword,
    changeEmail,

    connectSocialAccount,
    disconnectSocialAccount,

    confirmWithdraw,
  };
}
